import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { AudioPlaceholder } from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import { AudioPlayer } from '@lyearn/shared/components/Players';
import { Box } from '@lyearn/ui';

import Caption from '../../../components/ResizeContainer/Caption';
import { UploadButton } from '../../../components/UploadButton';
import { UploadCard } from '../../../components/UploadCard';
import useDrop from '../../../hooks/useDrop';
import useUpload, { FileType } from '../../../hooks/useUpload';
import { AudioComponentProps } from '../types';

const AudioComponentEditable: React.FC<AudioComponentProps> = (props) => {
  const { src, id, caption } = props;
  const { t } = useTranslation();

  const { node, updateAttrs, view } = useReactNodeView();

  const updateEditorState = useCallback(
    (_file: FileType) => {
      updateAttrs({
        src: _file.url,
      });
    },
    [updateAttrs],
  );

  const onLoad = useCallback(
    (duration: number) => {
      if (view?.editable && node?.attrs.duration !== Math.trunc(duration)) {
        updateAttrs({ duration });
      }
    },
    [node, updateAttrs, view],
  );

  const { uppy, currentFile } = useUpload(updateEditorState, node?.attrs.id);
  useDrop(node, updateAttrs, uppy);

  const isUploading = !!currentFile && !currentFile.url;

  return (
    <>
      {src ? (
        <Box className="mt-16 mb-12">
          <AudioPlayer url={src} onLoad={onLoad} />
          <Caption caption={caption} readOnly={false} />
        </Box>
      ) : isUploading ? (
        <div className="mt-16">
          <UploadCard
            isUploading
            PlaceholderIcon={AudioPlaceholder}
            name={currentFile?.name}
            progress={currentFile?.fileProgress as number}
          />
        </div>
      ) : (
        <UploadButton
          PlaceholderIcon={AudioPlaceholder}
          id={id as string}
          placeHolder={t('Lygo:UploadAudio')}
          type={'audio/*'}
        />
      )}
    </>
  );
};

export default AudioComponentEditable;
